/**
 * @file useHandleLogin.js
 * @module src/js/hooks/useHandleLogin
 * @description
 * **A hook to handle user login in the EDALab frontend.**
 * - Utilizes React contexts to show modal messages.
 * - Returns a function that:
 *   - Validates input fields for username and password.
 *   - Sends a POST request to the `/auth/login` endpoint with the provided credentials.
 *   - Manages loading state to prevent multiple submissions.
 *   - Displays success or error messages using the global modal system.
 *   - Updates the user context upon successful login.
 *
 * @version
 * <p>
 *  2025-12-02: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Migrate to useFetchFromBackend. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useCallback } from "react";
import { useUser } from "../contexts/UserProvider.js";
import { useModal } from "../contexts/ModalProvider.js";
import useFetchFromBackend from "./useFetchFromBackend";

export default function useHandleLogin({
    setUsername,
    setPassword,
    usernameRef,
    passwordRef,
    setLoading
}) {
    const fetchFromBackend = useFetchFromBackend();
    const { setUid, setUgroup, setLastLogin } = useUser();
    const { showModalMessage } = useModal();

    if (typeof setUsername !== 'function' ||
        typeof setPassword !== 'function' ||
        typeof setLoading !== 'function') {
        throw new Error("setUsername, setPassword, setLoading must be functions and usernameRef, passwordRef must be provided.");
    }
    if (!usernameRef || !passwordRef) {
        throw new Error("usernameRef and passwordRef must be provided.");
    }

    const handleLogin = useCallback(async ({
        username,
        password,
    }) => {
        try {
            if (!username || !password) {
                showModalMessage({ type: "error", content: "Please enter both username and password." });
                if (!username && usernameRef.current) {
                    usernameRef.current.focus();
                } else if (!password && passwordRef.current) {
                    passwordRef.current.focus();
                }
                return;
            }

            setLoading(true);
            showModalMessage({ type: "info", content: "Logging in..." });

            const data = await fetchFromBackend({
                request: "/auth/login",
                method: "POST",
                body: { username, password }
            });

            if (data && data.success) {
                setUid(data.uid);
                setUgroup(data.ugroup);
                setLastLogin(data.lastLogin || "N/A");
                showModalMessage({ type: "success", content: "Successfully logged in!" });
            } else if (data && data.error) {
                showModalMessage({ type: "error", content: data.error });
                if (data.error === "User not found or deleted.") {
                    setUsername("");
                    setPassword("");
                    if (usernameRef.current) {
                        usernameRef.current.focus();
                    }
                } else if (data.error === "Incorrect password.") {
                    setPassword("");
                    if (passwordRef.current) {
                        passwordRef.current.focus();
                    }
                }
            }
        } catch (error) {
            console.error("Failed to log in:", error);
            showModalMessage({ type: "error", content: "Failed to log in. Please try again." });

        } finally {
            setLoading(false);
        }

    }, [
        fetchFromBackend,
        setUid,
        setUgroup,
        setLastLogin,
        setUsername,
        setPassword,
        setLoading,
        showModalMessage,
        usernameRef,
        passwordRef,
    ]);

    return handleLogin;
}